import React, { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useThemeStore } from '@/stores';
import { 
  useRequests, 
  useRequestTypes, 
  useRequestStatuses, 
  useLocations, 
  useRequestDetails 
} from '@/hooks/useData';
import { requestService } from '@/services';
import { 
  Plus, 
  RefreshCw, 
  ChevronDown, 
  ChevronRight,
  ChevronLeft,
  User, 
  Copy, 
  Check, 
  FileEdit, 
  X, 
  CheckCircle2, 
  XCircle, 
  Clock, 
  MessageSquare, 
  Send, 
  Cpu, 
  Activity,
  Calendar,
  Filter,
  Search,
  CheckSquare,
  SlidersHorizontal
} from 'lucide-react';

const statusBadgeStyles: Record<string, { bg: string; color: string; border: string; label: string }> = {
  Done: { bg: 'rgba(16,185,129,0.15)', color: '#10b981', border: 'rgba(16,185,129,0.3)', label: 'منجز' },
  DoneWithErrors: { bg: 'rgba(245,158,11,0.15)', color: '#f59e0b', border: 'rgba(245,158,11,0.3)', label: 'منجز بأخطاء' },
  InProgress: { bg: 'rgba(59,130,246,0.15)', color: '#3b82f6', border: 'rgba(59,130,246,0.3)', label: 'قيد التنفيذ' },
  'In Progress': { bg: 'rgba(59,130,246,0.15)', color: '#3b82f6', border: 'rgba(59,130,246,0.3)', label: 'قيد التنفيذ' },
  New: { bg: 'rgba(99,102,241,0.15)', color: '#6366f1', border: 'rgba(99,102,241,0.3)', label: 'جديد' },
  Error: { bg: 'rgba(239,68,68,0.15)', color: '#ef4444', border: 'rgba(239,68,68,0.3)', label: 'خطأ' },
  Cancelled: { bg: 'rgba(100,116,139,0.15)', color: '#64748b', border: 'rgba(100,116,139,0.3)', label: 'ملغى' },
};

const RequestsPage: React.FC = () => {
  const { t } = useTranslation();
  const theme = useThemeStore((s) => s.theme);
  const isLight = theme === 'light';

  // Responsive Screen Detection
  const [isMobile, setIsMobile] = useState<boolean>(window.innerWidth < 768);
  const [showMobileFilter, setShowMobileFilter] = useState<boolean>(false);

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Filter Active State & Pagination
  const [pageNumber, setPageNumber] = useState(1);
  const pageSize = 10;
  const [sortOrder, setSortOrder] = useState<'desc' | 'asc'>('desc');

  // Filter Form Draft State
  const [draftOpenRequests, setDraftOpenRequests] = useState(false);
  const [draftClosedRequests, setDraftClosedRequests] = useState(false);
  const [draftActionRequired, setDraftActionRequired] = useState(false);
  const [draftPhoneVerification, setDraftPhoneVerification] = useState(false);

  const [draftStatus, setDraftStatus] = useState<string>('');
  const [draftTypeId, setDraftTypeId] = useState<string>('');
  const [draftCustomerSearch, setDraftCustomerSearch] = useState<string>('');
  const [draftZone, setDraftZone] = useState<string>('');
  const [draftAgent, setDraftAgent] = useState<string>('');
  const [draftStartDate, setDraftStartDate] = useState<string>('');
  const [draftEndDate, setDraftEndDate] = useState<string>('');

  // Applied Filter State
  const [appliedFilters, setAppliedFilters] = useState({
    open: false,
    closed: false,
    actionReq: false,
    phoneVer: false,
    status: 'All',
    typeId: '',
    customer: '',
    zone: '',
    agent: '',
    start: '',
    end: ''
  });

  // Header Switch
  const [includeSubPartners, setIncludeSubPartners] = useState(true);

  // Selected Request & Details State
  const [selectedRequestId, setSelectedRequestId] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  // Comment submit state
  const [newComment, setNewComment] = useState('');
  const [isSubmittingComment, setIsSubmittingComment] = useState(false);

  // Calculate exact API status parameter to send to FTTH portal
  const apiStatusParam = useMemo(() => {
    if (appliedFilters.open && !appliedFilters.closed) return 'Open';
    if (appliedFilters.closed && !appliedFilters.open) return 'Closed';
    if (appliedFilters.status && appliedFilters.status !== 'All') return appliedFilters.status;
    return 'All';
  }, [appliedFilters]);

  // Build additionalFilters array to send to FTTH portal
  const additionalFiltersParam = useMemo(() => {
    const list: string[] = [];
    if (appliedFilters.actionReq) {
      list.push('ActionRequired');
    }
    if (appliedFilters.phoneVer) {
      list.push('PhoneVerificationRequired');
    }
    return list;
  }, [appliedFilters]);

  // API Hooks
  const { data: requestsData, isLoading, isFetching, refetch } = useRequests({
    pageSize,
    pageNumber,
    status: apiStatusParam,
    typeIds: appliedFilters.typeId || undefined,
    additionalFilters: additionalFiltersParam,
  });

  const { data: rawTypes } = useRequestTypes();
  const { data: rawStatuses } = useRequestStatuses();
  const { data: rawZones } = useLocations();

  const { data: detailsData, isLoading: isLoadingDetails, refetch: refetchDetails } = useRequestDetails(selectedRequestId || undefined);

  // Normalizations
  const items = useMemo(() => {
    const raw = (requestsData as any)?.items || (requestsData as any)?.data || [];
    if (!Array.isArray(raw)) return [];
    return raw;
  }, [requestsData]);

  const totalCount = (requestsData as any)?.totalCount || items.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  const startRecord = (pageNumber - 1) * pageSize + 1;
  const endRecord = Math.min(pageNumber * pageSize, totalCount);

  const requestTypes = useMemo(() => {
    const raw = (rawTypes as any)?.items || (rawTypes as any)?.data || [];
    if (!Array.isArray(raw)) return [];
    return raw;
  }, [rawTypes]);

  const requestStatuses = useMemo(() => {
    const raw = (rawStatuses as any)?.items || (rawStatuses as any)?.data || [];
    if (!Array.isArray(raw)) return [];
    return raw;
  }, [rawStatuses]);

  const zonesList = useMemo(() => {
    const raw = (rawZones as any)?.items || (rawZones as any)?.data || [];
    if (!Array.isArray(raw)) return [];
    return raw;
  }, [rawZones]);

  // Client-side filtering mapping the portal's active filters exactly
  const filteredItems = useMemo(() => {
    return items.filter((item: any) => {
      // Customer name filter
      if (appliedFilters.customer.trim()) {
        const cName = (item.customer?.displayValue || '').toLowerCase();
        if (!cName.includes(appliedFilters.customer.trim().toLowerCase())) return false;
      }
      // Zone filter
      if (appliedFilters.zone) {
        const zId = item.zone?.id || item.zone?.displayValue || '';
        if (zId !== appliedFilters.zone) return false;
      }
      // Agent/Partner filter
      if (appliedFilters.agent.trim()) {
        const pName = (item.partner?.displayValue || item.createdBy?.displayValue || '').toLowerCase();
        if (!pName.includes(appliedFilters.agent.trim().toLowerCase())) return false;
      }
      return true;
    });
  }, [items, appliedFilters]);

  // KPI Summary Stats Calculation
  const stats = useMemo(() => {
    const total = totalCount || items.length;
    let newCount = 0;
    let inProgressCount = 0;
    let completedCount = 0;
    let cancelledCount = 0;

    items.forEach((r: any) => {
      const s = (r.status?.displayValue || r.status || '').toLowerCase();
      if (s.includes('new') || s.includes('open') || s.includes('pending')) {
        newCount++;
      } else if (s.includes('progress') || s.includes('processing')) {
        inProgressCount++;
      } else if (s.includes('done') || s.includes('close') || s.includes('complete')) {
        completedCount++;
      } else if (s.includes('cancel') || s.includes('reject') || s.includes('error')) {
        cancelledCount++;
      } else {
        newCount++;
      }
    });

    return { total, newCount, inProgressCount, completedCount, cancelledCount };
  }, [items, totalCount]);

  const handleApplyFilter = () => {
    setPageNumber(1);
    setAppliedFilters({
      open: draftOpenRequests,
      closed: draftClosedRequests,
      actionReq: draftActionRequired,
      phoneVer: draftPhoneVerification,
      status: draftStatus || 'All',
      typeId: draftTypeId,
      customer: draftCustomerSearch,
      zone: draftZone,
      agent: draftAgent,
      start: draftStartDate,
      end: draftEndDate
    });
    if (isMobile) setShowMobileFilter(false);
    refetch();
  };

  const handleResetFilter = () => {
    setDraftOpenRequests(false);
    setDraftClosedRequests(false);
    setDraftActionRequired(false);
    setDraftPhoneVerification(false);
    setDraftStatus('');
    setDraftTypeId('');
    setDraftCustomerSearch('');
    setDraftZone('');
    setDraftAgent('');
    setDraftStartDate('');
    setDraftEndDate('');

    setPageNumber(1);
    setAppliedFilters({
      open: false,
      closed: false,
      actionReq: false,
      phoneVer: false,
      status: 'All',
      typeId: '',
      customer: '',
      zone: '',
      agent: '',
      start: '',
      end: ''
    });
    if (isMobile) setShowMobileFilter(false);
    refetch();
  };

  const copyToClipboard = (text: string, e: React.MouseEvent) => {
    e.stopPropagation();
    navigator.clipboard.writeText(text);
    setCopiedId(text);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const handleAddComment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedRequestId || !newComment.trim()) return;

    setIsSubmittingComment(true);
    try {
      await requestService.addComment(selectedRequestId, newComment.trim());
      setNewComment('');
      refetchDetails();
    } catch (err) {
      console.error('Failed to submit comment:', err);
    } finally {
      setIsSubmittingComment(false);
    }
  };

  const selectedItem = useMemo(() => {
    if (!selectedRequestId) return null;
    return items.find((i: any) => (i.id || i.self?.id) === selectedRequestId);
  }, [items, selectedRequestId]);

  // Color & Theme Palette Definitions for Light and Dark Modes
  const bgColor = isLight ? '#f8fafc' : '#050f1e';
  const cardBg = isLight ? '#ffffff' : 'rgba(13,31,60,0.7)';
  const cardSelectedBg = isLight ? '#f1f5f9' : 'rgba(13,31,60,0.95)';
  const borderColor = isLight ? '#e2e8f0' : 'rgba(255,255,255,0.08)';
  const textPrimary = isLight ? '#0f172a' : '#ffffff';
  const textSecondary = isLight ? '#475569' : '#94a3b8';
  const textMuted = isLight ? '#64748b' : '#64748b';
  const inputBg = isLight ? '#ffffff' : '#050f1e';
  const inputBorder = isLight ? '#cbd5e1' : 'rgba(255,255,255,0.12)';

  // Render Filter Panel Component (Reusable for Sidebar & Mobile Dropdown/Modal)
  const renderFilterPanel = () => (
    <div
      style={{
        background: isLight ? '#ffffff' : '#0d1f3c',
        border: isLight ? '1px solid #cbd5e1' : '1px solid rgba(255,255,255,0.08)',
        borderRadius: '12px',
        padding: '20px',
        display: 'flex',
        flexDirection: 'column',
        gap: '18px',
        direction: 'rtl',
        boxShadow: isLight ? '0 4px 20px rgba(0,0,0,0.05)' : 'none',
      }}
    >
      {/* Title */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h2 style={{ color: textPrimary, fontSize: '18px', fontWeight: 700, margin: 0 }}>
          تصفية
        </h2>
        {isMobile && (
          <button
            onClick={() => setShowMobileFilter(false)}
            style={{ background: 'transparent', border: 'none', color: textSecondary, cursor: 'pointer' }}
          >
            <X size={20} />
          </button>
        )}
      </div>

      {/* Display / Show Checkboxes Group */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        <span style={{ fontSize: '12px', color: textSecondary, fontWeight: 600 }}>عرض</span>
        
        {/* Row 1: Open & Closed Requests */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', fontSize: '12px', color: textPrimary }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={draftOpenRequests}
              onChange={(e) => {
                setDraftOpenRequests(e.target.checked);
                if (e.target.checked) setDraftClosedRequests(false);
              }}
              style={{ width: '16px', height: '16px', accentColor: '#ef4444', cursor: 'pointer' }}
            />
            <span>الطلبات المفتوحه</span>
          </label>

          <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={draftClosedRequests}
              onChange={(e) => {
                setDraftClosedRequests(e.target.checked);
                if (e.target.checked) setDraftOpenRequests(false);
              }}
              style={{ width: '16px', height: '16px', accentColor: '#ef4444', cursor: 'pointer' }}
            />
            <span>الطلبات المغلقة</span>
          </label>
        </div>

        {/* Row 2: Action Required */}
        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', color: textPrimary, cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={draftActionRequired}
            onChange={(e) => setDraftActionRequired(e.target.checked)}
            style={{ width: '16px', height: '16px', accentColor: '#ef4444', cursor: 'pointer' }}
          />
          <span>مطلوب اتخاذ إجراء</span>
        </label>

        {/* Row 3: Phone Verification Required */}
        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', color: textPrimary, cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={draftPhoneVerification}
            onChange={(e) => setDraftPhoneVerification(e.target.checked)}
            style={{ width: '16px', height: '16px', accentColor: '#ef4444', cursor: 'pointer' }}
          />
          <span>مطلوب تفعيل رقم الهاتف</span>
        </label>
      </div>

      {/* Request Status Dropdown */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
        <label style={{ fontSize: '12px', color: textSecondary }}>حالة الطلب</label>
        <select
          value={draftStatus}
          onChange={(e) => setDraftStatus(e.target.value)}
          style={{
            width: '100%',
            background: inputBg,
            border: `1px solid ${inputBorder}`,
            borderRadius: '6px',
            padding: '9px 12px',
            color: textPrimary,
            fontSize: '13px',
            outline: 'none',
            direction: 'rtl',
          }}
        >
          <option value="" style={{ color: textPrimary, background: inputBg }}>اختر الحالة</option>
          <option value="All" style={{ color: textPrimary, background: inputBg }}>جميع الحالات</option>
          <option value="Open" style={{ color: textPrimary, background: inputBg }}>الطلبات المفتوحة</option>
          <option value="Closed" style={{ color: textPrimary, background: inputBg }}>الطلبات المغلقة</option>
          {requestStatuses.map((st: any) => (
            <option key={st.id} value={st.displayValue || st.id} style={{ color: textPrimary, background: inputBg }}>
              {st.displayValue || st.name}
            </option>
          ))}
        </select>
      </div>

      {/* Request Type Dropdown */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
        <label style={{ fontSize: '12px', color: textSecondary }}>نوع الطلب</label>
        <select
          value={draftTypeId}
          onChange={(e) => setDraftTypeId(e.target.value)}
          style={{
            width: '100%',
            background: inputBg,
            border: `1px solid ${inputBorder}`,
            borderRadius: '6px',
            padding: '9px 12px',
            color: textPrimary,
            fontSize: '13px',
            outline: 'none',
            direction: 'rtl',
          }}
        >
          <option value="" style={{ color: textPrimary, background: inputBg }}>اختر نوع الطلب</option>
          {requestTypes.map((tp: any) => (
            <option key={tp.id} value={tp.id} style={{ color: textPrimary, background: inputBg }}>
              {tp.displayValue || tp.name}
            </option>
          ))}
        </select>
      </div>

      {/* Customer Name Search */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
        <label style={{ fontSize: '12px', color: textSecondary }}>اسم المشترك</label>
        <input
          type="text"
          value={draftCustomerSearch}
          onChange={(e) => setDraftCustomerSearch(e.target.value)}
          placeholder="اسم المشترك"
          style={{
            width: '100%',
            background: inputBg,
            border: `1px solid ${inputBorder}`,
            borderRadius: '6px',
            padding: '9px 12px',
            color: textPrimary,
            fontSize: '13px',
            outline: 'none',
          }}
        />
      </div>

      {/* Section Subheader: Include Sub-partners */}
      <div style={{ borderTop: `1px solid ${borderColor}`, paddingTop: '10px', marginTop: '2px' }}>
        <span style={{ fontSize: '12px', color: textSecondary, fontWeight: 600 }}>تضمين الشركاء الفرعيين</span>
      </div>

      {/* Zone Input / Selector with Clear Button */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
        <label style={{ fontSize: '12px', color: textSecondary }}>المنطقة</label>
        <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
          <select
            value={draftZone}
            onChange={(e) => setDraftZone(e.target.value)}
            style={{
              width: '100%',
              background: inputBg,
              border: `1px solid ${inputBorder}`,
              borderRadius: '6px',
              padding: '9px 12px',
              paddingLeft: '32px',
              color: textPrimary,
              fontSize: '13px',
              outline: 'none',
              direction: 'rtl',
            }}
          >
            <option value="" style={{ color: textPrimary, background: inputBg }}>اختر المنطقة</option>
            <option value="FNS0037" style={{ color: textPrimary, background: inputBg }}>FNS0037</option>
            {zonesList.map((z: any) => {
              const zid = z.self?.id || z.id;
              return (
                <option key={zid} value={zid} style={{ color: textPrimary, background: inputBg }}>
                  {zid}
                </option>
              );
            })}
          </select>
          {draftZone && (
            <button
              onClick={() => setDraftZone('')}
              style={{
                position: 'absolute',
                left: '8px',
                background: 'transparent',
                border: 'none',
                color: textSecondary,
                cursor: 'pointer',
                fontSize: '14px',
              }}
              title="إلغاء اختيار المنطقة"
            >
              ×
            </button>
          )}
        </div>
      </div>

      {/* Agent / Partner Input */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
        <label style={{ fontSize: '12px', color: textSecondary }}>الوكيل</label>
        <input
          type="text"
          value={draftAgent}
          onChange={(e) => setDraftAgent(e.target.value)}
          placeholder="الوكيل"
          style={{
            width: '100%',
            background: inputBg,
            border: `1px solid ${inputBorder}`,
            borderRadius: '6px',
            padding: '9px 12px',
            color: textPrimary,
            fontSize: '13px',
            outline: 'none',
          }}
        />
      </div>

      {/* Creation Date Range Inputs */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
        <label style={{ fontSize: '12px', color: textSecondary }}>تاريخ الانشاء:</label>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
          <div>
            <span style={{ fontSize: '11px', color: textSecondary, display: 'block', marginBottom: '2px' }}>تاريخ البداية</span>
            <input
              type="date"
              value={draftStartDate}
              onChange={(e) => setDraftStartDate(e.target.value)}
              placeholder="DD/MM/YYYY"
              style={{
                width: '100%',
                background: inputBg,
                border: `1px solid ${inputBorder}`,
                borderRadius: '6px',
                padding: '7px 8px',
                color: textPrimary,
                fontSize: '11px',
                outline: 'none',
              }}
            />
          </div>

          <div>
            <span style={{ fontSize: '11px', color: textSecondary, display: 'block', marginBottom: '2px' }}>تاريخ النهاية</span>
            <input
              type="date"
              value={draftEndDate}
              onChange={(e) => setDraftEndDate(e.target.value)}
              placeholder="DD/MM/YYYY"
              style={{
                width: '100%',
                background: inputBg,
                border: `1px solid ${inputBorder}`,
                borderRadius: '6px',
                padding: '7px 8px',
                color: textPrimary,
                fontSize: '11px',
                outline: 'none',
              }}
            />
          </div>
        </div>
      </div>

      {/* Apply Filter Button Matching Portal Style */}
      <button
        onClick={handleApplyFilter}
        style={{
          width: '100%',
          background: isLight ? '#ef4444' : 'rgba(239, 68, 68, 0.25)',
          color: '#ffffff',
          border: isLight ? 'none' : '1px solid rgba(239, 68, 68, 0.4)',
          borderRadius: '8px',
          padding: '12px',
          fontSize: '14px',
          fontWeight: 700,
          cursor: 'pointer',
          textAlign: 'center',
          marginTop: '6px',
          transition: 'all 0.15s',
          boxShadow: isLight ? '0 4px 12px rgba(239,68,68,0.25)' : 'none',
        }}
      >
        تطبيق التصفية
      </button>

      {/* Reset All Filters Text Button */}
      <button
        onClick={handleResetFilter}
        style={{
          background: 'transparent',
          border: 'none',
          color: textSecondary,
          fontSize: '13px',
          fontWeight: 500,
          cursor: 'pointer',
          textAlign: 'center',
          padding: '4px',
          textDecoration: 'underline',
        }}
      >
        مسح الكل
      </button>
    </div>
  );

  return (
    <div style={{ padding: isMobile ? '12px' : '20px', direction: 'rtl', minHeight: '100vh', background: bgColor, color: textPrimary }}>
      
      {/* Top Header Bar (With Clean Title & Mobile Filter Action Button) */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <h1 style={{ color: textPrimary, fontSize: isMobile ? '20px' : '24px', fontWeight: 700, margin: 0 }}>
            الطلبات
          </h1>
        </div>

        {/* Top Header Controls: Sub-Partners Checkbox & Mobile Filter Button */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', fontSize: '13px', color: textSecondary }}>
          {/* Mobile Filter Toggle Button (Matching Subscriber Sessions style) */}
          <button
            onClick={() => setShowMobileFilter(!showMobileFilter)}
            style={{
              display: isMobile ? 'flex' : 'none',
              alignItems: 'center',
              gap: '6px',
              background: isLight ? '#ffffff' : 'rgba(59,130,246,0.15)',
              border: isLight ? '1px solid #cbd5e1' : '1px solid rgba(59,130,246,0.3)',
              borderRadius: '8px',
              padding: '8px 14px',
              color: '#3b82f6',
              fontSize: '13px',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            <SlidersHorizontal size={15} />
            <span>خيارات التصفية</span>
          </button>

          <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontSize: isMobile ? '12px' : '13px' }}>
            <span>تضمين الشركاء الفرعيين</span>
            <input
              type="checkbox"
              checked={includeSubPartners}
              onChange={(e) => setIncludeSubPartners(e.target.checked)}
              style={{ width: '16px', height: '16px', accentColor: '#10b981', cursor: 'pointer' }}
            />
          </label>
        </div>
      </div>

      {/* Mobile Filter Collapsible Modal/Drawer */}
      {isMobile && showMobileFilter && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            background: 'rgba(0,0,0,0.65)',
            backdropFilter: 'blur(4px)',
            zIndex: 999,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '16px',
          }}
          onClick={() => setShowMobileFilter(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: '100%',
              maxWidth: '420px',
              maxHeight: '90vh',
              overflowY: 'auto',
              borderRadius: '14px',
            }}
          >
            {renderFilterPanel()}
          </div>
        </div>
      )}

      {/* KPI Summary Stats Cards Grid */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
          gap: '14px',
          marginBottom: '20px',
        }}
      >
        <div
          style={{
            background: isLight ? '#ffffff' : 'linear-gradient(135deg, rgba(30, 41, 59, 0.7) 0%, rgba(15, 23, 42, 0.8) 100%)',
            border: isLight ? '1px solid #e2e8f0' : '1px solid rgba(255,255,255,0.08)',
            borderRadius: '12px',
            padding: '16px 18px',
            display: 'flex',
            alignItems: 'center',
            gap: '14px',
            boxShadow: isLight ? '0 2px 8px rgba(0,0,0,0.04)' : 'none',
          }}
        >
          <div style={{ width: '40px', height: '40px', borderRadius: '10px', background: 'rgba(99,102,241,0.15)', color: '#818cf8', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Activity size={20} />
          </div>
          <div>
            <span style={{ fontSize: '12px', color: textSecondary, fontWeight: 500 }}>إجمالي الطلبات</span>
            <div style={{ fontSize: '20px', fontWeight: 800, color: textPrimary, marginTop: '2px' }}>{stats.total}</div>
          </div>
        </div>

        <div
          style={{
            background: isLight ? '#ffffff' : 'linear-gradient(135deg, rgba(30, 41, 59, 0.7) 0%, rgba(15, 23, 42, 0.8) 100%)',
            border: isLight ? '1px solid #e2e8f0' : '1px solid rgba(255,255,255,0.08)',
            borderRadius: '12px',
            padding: '16px 18px',
            display: 'flex',
            alignItems: 'center',
            gap: '14px',
            boxShadow: isLight ? '0 2px 8px rgba(0,0,0,0.04)' : 'none',
          }}
        >
          <div style={{ width: '40px', height: '40px', borderRadius: '10px', background: 'rgba(245,158,11,0.15)', color: '#fbbf24', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Clock size={20} />
          </div>
          <div>
            <span style={{ fontSize: '12px', color: textSecondary, fontWeight: 500 }}>جديدة / معلقة</span>
            <div style={{ fontSize: '20px', fontWeight: 800, color: '#fbbf24', marginTop: '2px' }}>{stats.newCount}</div>
          </div>
        </div>

        <div
          style={{
            background: isLight ? '#ffffff' : 'linear-gradient(135deg, rgba(30, 41, 59, 0.7) 0%, rgba(15, 23, 42, 0.8) 100%)',
            border: isLight ? '1px solid #e2e8f0' : '1px solid rgba(255,255,255,0.08)',
            borderRadius: '12px',
            padding: '16px 18px',
            display: 'flex',
            alignItems: 'center',
            gap: '14px',
            boxShadow: isLight ? '0 2px 8px rgba(0,0,0,0.04)' : 'none',
          }}
        >
          <div style={{ width: '40px', height: '40px', borderRadius: '10px', background: 'rgba(59,130,246,0.15)', color: '#60a5fa', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Cpu size={20} />
          </div>
          <div>
            <span style={{ fontSize: '12px', color: textSecondary, fontWeight: 500 }}>قيد التنفيذ</span>
            <div style={{ fontSize: '20px', fontWeight: 800, color: '#60a5fa', marginTop: '2px' }}>{stats.inProgressCount}</div>
          </div>
        </div>

        <div
          style={{
            background: isLight ? '#ffffff' : 'linear-gradient(135deg, rgba(30, 41, 59, 0.7) 0%, rgba(15, 23, 42, 0.8) 100%)',
            border: isLight ? '1px solid #e2e8f0' : '1px solid rgba(255,255,255,0.08)',
            borderRadius: '12px',
            padding: '16px 18px',
            display: 'flex',
            alignItems: 'center',
            gap: '14px',
            boxShadow: isLight ? '0 2px 8px rgba(0,0,0,0.04)' : 'none',
          }}
        >
          <div style={{ width: '40px', height: '40px', borderRadius: '10px', background: 'rgba(16,185,129,0.15)', color: '#34d399', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <CheckCircle2 size={20} />
          </div>
          <div>
            <span style={{ fontSize: '12px', color: textSecondary, fontWeight: 500 }}>منجزة / مكتملة</span>
            <div style={{ fontSize: '20px', fontWeight: 800, color: '#34d399', marginTop: '2px' }}>{stats.completedCount}</div>
          </div>
        </div>

        <div
          style={{
            background: isLight ? '#ffffff' : 'linear-gradient(135deg, rgba(30, 41, 59, 0.7) 0%, rgba(15, 23, 42, 0.8) 100%)',
            border: isLight ? '1px solid #e2e8f0' : '1px solid rgba(255,255,255,0.08)',
            borderRadius: '14px',
            padding: '16px 18px',
            display: 'flex',
            alignItems: 'center',
            gap: '14px',
            boxShadow: isLight ? '0 2px 8px rgba(0,0,0,0.04)' : 'none',
          }}
        >
          <div style={{ width: '40px', height: '40px', borderRadius: '10px', background: 'rgba(239,68,68,0.15)', color: '#f87171', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <XCircle size={20} />
          </div>
          <div>
            <span style={{ fontSize: '12px', color: textSecondary, fontWeight: 500 }}>ملغاة / خطأ</span>
            <div style={{ fontSize: '20px', fontWeight: 800, color: '#f87171', marginTop: '2px' }}>{stats.cancelledCount}</div>
          </div>
        </div>
      </div>

      {/* Main Grid: Right (Requests Cards List) & Left (Filter Sidebar Panel Desktop) */}
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'minmax(0, 1fr) 300px', gap: '20px', alignItems: 'flex-start' }}>
        
        {/* RIGHT COLUMN: Requests Cards */}
        <div>
          {/* Header Controls: Count & Sort Dropdown */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', fontSize: '13px', color: textSecondary, flexWrap: 'wrap', gap: '10px' }}>
            <div>
              عرض <strong style={{ color: textPrimary }}>{totalCount > 0 ? `${startRecord}-${endRecord}` : '0'}</strong> من اصل <strong style={{ color: textPrimary }}>{totalCount}</strong>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <button
                onClick={() => refetch()}
                style={{
                  background: isLight ? '#ffffff' : 'rgba(255,255,255,0.05)',
                  border: `1px solid ${inputBorder}`,
                  borderRadius: '6px',
                  padding: '6px 12px',
                  color: textSecondary,
                  cursor: 'pointer',
                  fontSize: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                }}
              >
                <RefreshCw size={13} style={isFetching ? { animation: 'spin 1s linear infinite' } : {}} />
                تحديث
              </button>

              <select
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value as 'desc' | 'asc')}
                style={{
                  background: inputBg,
                  border: `1px solid ${inputBorder}`,
                  borderRadius: '6px',
                  padding: '6px 14px',
                  color: textPrimary,
                  fontSize: '13px',
                  outline: 'none',
                  cursor: 'pointer',
                  direction: 'rtl',
                }}
              >
                <option value="desc" style={{ color: textPrimary, background: inputBg }}>ترتيب: وقت بدء الطلب تنازلي</option>
                <option value="asc" style={{ color: textPrimary, background: inputBg }}>ترتيب: وقت بدء الطلب تصاعدي</option>
              </select>
            </div>
          </div>

          {/* Cards List */}
          {isLoading ? (
            <div style={{ background: cardBg, borderRadius: '12px', padding: '50px', textAlign: 'center', color: textSecondary, border: `1px solid ${borderColor}` }}>
              جاري تحميل الطلبات حياً من البوابة...
            </div>
          ) : filteredItems.length === 0 ? (
            <div style={{ background: cardBg, borderRadius: '12px', padding: '50px', textAlign: 'center', color: textSecondary, border: `1px solid ${borderColor}` }}>
              لا توجد طلبات مطابقة للفلتر المحدد.
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              {filteredItems.map((req: any) => {
                const reqId = req.self?.id || req.id || '—';
                const typeName = req.type?.displayValue || req.type?.type?.displayValue || 'Customer Onboarding Request';
                const statusName = req.status?.displayValue || 'Done';
                const st = statusBadgeStyles[statusName] || statusBadgeStyles.Done;

                const customerName = req.customer?.displayValue || '—';
                const partnerName = req.partner?.displayValue || req.createdBy?.displayValue || 'علي شهيد سعدون فيصل';
                const zoneName = req.zone?.displayValue || req.zone?.id || 'FNS0037';
                const taskName = req.task?.displayValue || 'Install physical devices';

                const createdAtDate = req.createdAt ? new Date(req.createdAt).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '19.07.2026';
                const updatedDate = req.modifiedAt ? new Date(req.modifiedAt).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' }) : createdAtDate;

                const isSelected = selectedRequestId === reqId;

                return (
                  <div
                    key={reqId}
                    onClick={() => setSelectedRequestId(reqId)}
                    style={{
                      background: isSelected ? cardSelectedBg : cardBg,
                      border: isSelected ? '1px solid #3b82f6' : `1px solid ${borderColor}`,
                      borderRadius: '10px',
                      padding: isMobile ? '14px' : '16px 20px',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      boxShadow: isLight ? '0 2px 8px rgba(0,0,0,0.04)' : (isSelected ? '0 0 15px rgba(59,130,246,0.2)' : 'none'),
                    }}
                  >
                    {/* Card Top Row: Type & Copyable Reference ID */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', borderBottom: `1px dashed ${borderColor}`, paddingBottom: '8px', flexWrap: 'wrap', gap: '6px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ color: textPrimary, fontWeight: 700, fontSize: isMobile ? '13px' : '14px' }}>
                          {typeName}
                        </span>
                      </div>

                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: textSecondary }}>
                        <span>الرقم التعريفي</span>
                        <span style={{ color: textPrimary, fontFamily: 'monospace', direction: 'ltr', fontSize: isMobile ? '11px' : '12px' }}>
                          {isMobile ? `${reqId.substring(0, 14)}...` : reqId}
                        </span>
                        <button
                          onClick={(e) => copyToClipboard(reqId, e)}
                          title="نسخ الرقم التعريفي"
                          style={{
                            background: 'transparent',
                            border: 'none',
                            color: copiedId === reqId ? '#10b981' : textSecondary,
                            cursor: 'pointer',
                            padding: '2px',
                            display: 'flex',
                            alignItems: 'center',
                          }}
                        >
                          {copiedId === reqId ? <Check size={14} /> : <Copy size={14} />}
                        </button>
                      </div>
                    </div>

                    {/* Card Main Body Grid: Responsive for Mobile & Desktop */}
                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns: isMobile ? '1fr 1fr' : '1.1fr 1.3fr 1.3fr 1fr 1fr',
                        gap: '12px',
                        alignItems: 'center',
                      }}
                    >
                      {/* Col 1: Status & Updated */}
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        <span style={{ fontSize: '11px', color: textMuted }}>الحالة</span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span
                            style={{
                              background: st.bg,
                              color: st.color,
                              border: `1px solid ${st.border}`,
                              padding: '2px 10px',
                              borderRadius: '12px',
                              fontSize: '12px',
                              fontWeight: 600,
                            }}
                          >
                            {st.label}
                          </span>
                        </div>
                        <span style={{ fontSize: '11px', color: textMuted, marginTop: '2px' }}>
                          تحدث <span style={{ color: textSecondary, direction: 'ltr', display: 'inline-block' }}>{updatedDate}</span>
                        </span>
                      </div>

                      {/* Col 2: Zone & Associated Task */}
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        <span style={{ fontSize: '11px', color: textMuted }}>المنطقة</span>
                        <strong style={{ color: '#3b82f6', fontSize: '13px', fontWeight: 600 }}>{zoneName}</strong>
                        <span style={{ fontSize: '11px', color: textMuted, marginTop: '2px' }}>
                          المهام المرتبطة
                        </span>
                        <span style={{ fontSize: '12px', color: textSecondary }}>{taskName}</span>
                      </div>

                      {/* Col 3: Agent / Partner */}
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        <span style={{ fontSize: '11px', color: textMuted }}>الوكيل</span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: textPrimary, fontSize: '13px', fontWeight: 500 }}>
                          <User size={14} style={{ color: textMuted }} />
                          <span>{partnerName}</span>
                        </div>
                      </div>

                      {/* Col 4: Customer Name */}
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        <span style={{ fontSize: '11px', color: textMuted }}>اسم المشترك</span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: textPrimary, fontSize: '13px', fontWeight: 600 }}>
                          <User size={14} style={{ color: '#3b82f6' }} />
                          <span>{customerName}</span>
                        </div>
                      </div>

                      {/* Col 5: Created At & Left Quick Action Icons */}
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gridColumn: isMobile ? '1 / -1' : 'auto' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                          <span style={{ fontSize: '11px', color: textMuted }}>أنشأت</span>
                          <span style={{ color: textSecondary, fontSize: '13px', direction: 'ltr' }}>{createdAtDate}</span>
                        </div>

                        {/* Details Action Button */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <button
                            onClick={(e) => { e.stopPropagation(); setSelectedRequestId(reqId); }}
                            style={{
                              background: isLight ? '#e2e8f0' : 'rgba(255,255,255,0.06)',
                              border: 'none',
                              color: textPrimary,
                              cursor: 'pointer',
                              padding: '6px 12px',
                              borderRadius: '6px',
                              fontSize: '12px',
                              fontWeight: 600,
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px',
                            }}
                            title="تفاصيل الطلب"
                          >
                            <FileEdit size={14} />
                            <span>التفاصيل</span>
                          </button>
                        </div>
                      </div>

                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Pagination Bar Matching Portal Standards */}
          {totalCount > 0 && (
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginTop: '20px',
                padding: '14px 20px',
                background: cardBg,
                border: `1px solid ${borderColor}`,
                borderRadius: '10px',
                color: textSecondary,
                fontSize: '13px',
                flexWrap: 'wrap',
                gap: '10px',
              }}
            >
              <div>
                صفحة <strong style={{ color: textPrimary }}>{pageNumber}</strong> من <strong style={{ color: textPrimary }}>{totalPages}</strong>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <button
                  disabled={pageNumber <= 1 || isFetching}
                  onClick={() => setPageNumber((p) => Math.max(1, p - 1))}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    background: pageNumber <= 1 ? (isLight ? '#f1f5f9' : 'rgba(255,255,255,0.02)') : (isLight ? '#ffffff' : 'rgba(255,255,255,0.06)'),
                    border: `1px solid ${inputBorder}`,
                    borderRadius: '6px',
                    padding: '6px 14px',
                    color: pageNumber <= 1 ? textMuted : textPrimary,
                    cursor: pageNumber <= 1 ? 'not-allowed' : 'pointer',
                    fontSize: '12px',
                    fontWeight: 600,
                  }}
                >
                  <ChevronRight size={16} />
                  السابق
                </button>

                {/* Page Jump buttons */}
                <div style={{ display: 'flex', gap: '4px' }}>
                  {[...Array(Math.min(5, totalPages))].map((_, i) => {
                    let pageNum = pageNumber;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (pageNumber <= 3) {
                      pageNum = i + 1;
                    } else if (pageNumber >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = pageNumber - 2 + i;
                    }

                    return (
                      <button
                        key={pageNum}
                        onClick={() => setPageNumber(pageNum)}
                        style={{
                          width: '32px',
                          height: '32px',
                          borderRadius: '6px',
                          border: pageNumber === pageNum ? '1px solid #3b82f6' : `1px solid ${inputBorder}`,
                          background: pageNumber === pageNum ? '#3b82f6' : (isLight ? '#ffffff' : 'rgba(255,255,255,0.04)'),
                          color: pageNumber === pageNum ? '#ffffff' : textPrimary,
                          fontWeight: pageNumber === pageNum ? 700 : 500,
                          cursor: 'pointer',
                          fontSize: '12px',
                        }}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>

                <button
                  disabled={pageNumber >= totalPages || isFetching}
                  onClick={() => setPageNumber((p) => Math.min(totalPages, p + 1))}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    background: pageNumber >= totalPages ? (isLight ? '#f1f5f9' : 'rgba(255,255,255,0.02)') : (isLight ? '#ffffff' : 'rgba(255,255,255,0.06)'),
                    border: `1px solid ${inputBorder}`,
                    borderRadius: '6px',
                    padding: '6px 14px',
                    color: pageNumber >= totalPages ? textMuted : textPrimary,
                    cursor: pageNumber >= totalPages ? 'not-allowed' : 'pointer',
                    fontSize: '12px',
                    fontWeight: 600,
                  }}
                >
                  التالي
                  <ChevronLeft size={16} />
                </button>
              </div>
            </div>
          )}

        </div>

        {/* LEFT COLUMN: Filter Sidebar Panel Desktop Only */}
        {!isMobile && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {/* Create Request Big Red Button */}
            <button
              style={{
                width: '100%',
                background: '#ef4444',
                color: '#ffffff',
                border: 'none',
                borderRadius: '8px',
                padding: '14px',
                fontSize: '15px',
                fontWeight: 700,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                boxShadow: '0 4px 15px rgba(239, 68, 68, 0.3)',
                transition: 'transform 0.15s, background 0.15s',
              }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = '#dc2626'; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = '#ef4444'; }}
            >
              <Plus size={18} />
              إنشاء طلب
            </button>

            {/* Filter Panel Box Desktop */}
            {renderFilterPanel()}
          </div>
        )}

      </div>

      {/* Request Details Drawer */}
      {selectedRequestId && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            background: 'rgba(0,0,0,0.7)',
            backdropFilter: 'blur(5px)',
            zIndex: 1000,
            display: 'flex',
            justifyContent: 'flex-end',
          }}
          onClick={() => setSelectedRequestId(null)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: '100%',
              maxWidth: '580px',
              height: '100%',
              background: isLight ? '#ffffff' : '#091526',
              borderRight: `1px solid ${borderColor}`,
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '-10px 0 30px rgba(0,0,0,0.8)',
              direction: 'rtl',
              overflowY: 'auto',
              color: textPrimary,
            }}
          >
            {/* Drawer Header */}
            <div style={{ padding: '20px', background: isLight ? '#f1f5f9' : '#0d1f3c', borderBottom: `1px solid ${borderColor}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <h2 style={{ color: textPrimary, fontSize: '18px', fontWeight: 700, margin: 0 }}>
                  تفاصيل الطلب #{selectedRequestId.substring(0, 8)}
                </h2>
                <span style={{ fontSize: '12px', color: textSecondary }}>
                  {selectedItem?.type?.displayValue || 'Customer Onboarding Request'}
                </span>
              </div>
              <button
                onClick={() => setSelectedRequestId(null)}
                style={{ background: isLight ? '#e2e8f0' : 'rgba(255,255,255,0.05)', border: 'none', color: textSecondary, borderRadius: '50%', width: '32px', height: '32px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              >
                <X size={18} />
              </button>
            </div>

            {/* Drawer Content */}
            <div style={{ padding: '20px', flex: 1 }}>
              {isLoadingDetails ? (
                <div style={{ textAlign: 'center', padding: '40px', color: textSecondary }}>
                  جاري جلب تفاصيل وسجل الطلب الكامل من البوابة...
                </div>
              ) : (
                <>
                  {/* Customer Info Card */}
                  <div style={{ background: isLight ? '#f8fafc' : 'rgba(255,255,255,0.03)', border: `1px solid ${borderColor}`, borderRadius: '10px', padding: '14px', marginBottom: '16px' }}>
                    <h3 style={{ color: '#3b82f6', fontSize: '14px', fontWeight: 600, margin: '0 0 10px 0', display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <User size={15} /> معلومات المشترك والاتصال
                    </h3>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', fontSize: '13px' }}>
                      <div>
                        <span style={{ color: textSecondary }}>اسم المشترك: </span>
                        <strong style={{ color: textPrimary }}>
                          {detailsData?.onboarding?.model?.customer?.self?.displayValue || selectedItem?.customer?.displayValue || '—'}
                        </strong>
                      </div>
                      <div>
                        <span style={{ color: textSecondary }}>رقم الهاتف: </span>
                        <strong style={{ color: '#10b981', direction: 'ltr', display: 'inline-block' }}>
                          {detailsData?.onboarding?.model?.customer?.contact?.mobile || selectedItem?.customer?.contact?.mobile || '—'}
                        </strong>
                      </div>
                      <div>
                        <span style={{ color: textSecondary }}>اسم المستخدم: </span>
                        <strong style={{ color: '#d97706', fontFamily: 'monospace' }}>
                          {selectedItem?.subscriptionUsername || '—'}
                        </strong>
                      </div>
                      <div>
                        <span style={{ color: textSecondary }}>رمز التركيب: </span>
                        <strong style={{ color: '#6366f1', fontFamily: 'monospace' }}>
                          {selectedItem?.installationCode || '—'}
                        </strong>
                      </div>
                    </div>
                  </div>

                  {/* Tasks Section */}
                  {detailsData?.tasks?.items && detailsData.tasks.items.length > 0 && (
                    <div style={{ background: isLight ? '#f8fafc' : 'rgba(255,255,255,0.03)', border: `1px solid ${borderColor}`, borderRadius: '10px', padding: '14px', marginBottom: '16px' }}>
                      <h3 style={{ color: '#d97706', fontSize: '14px', fontWeight: 600, margin: '0 0 10px 0', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <Cpu size={15} /> المهام المرتبطة بالطلب
                      </h3>
                      {detailsData.tasks.items.map((tk: any, idx: number) => (
                        <div key={idx} style={{ padding: '8px 10px', background: isLight ? '#e2e8f0' : 'rgba(0,0,0,0.2)', borderRadius: '6px', fontSize: '13px', color: textPrimary, marginBottom: '6px' }}>
                          📌 {tk.self?.displayValue || 'Install physical devices'}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Logs / Audit Trail */}
                  <div style={{ background: isLight ? '#f8fafc' : 'rgba(255,255,255,0.03)', border: `1px solid ${borderColor}`, borderRadius: '10px', padding: '14px', marginBottom: '16px' }}>
                    <h3 style={{ color: '#6366f1', fontSize: '14px', fontWeight: 600, margin: '0 0 10px 0', display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <Activity size={15} /> سجل الحركات والعمليات (Audit Logs)
                    </h3>
                    {detailsData?.logs?.items && detailsData.logs.items.length > 0 ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        {detailsData.logs.items.map((lg: any, idx: number) => (
                          <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 10px', background: isLight ? '#e2e8f0' : 'rgba(0,0,0,0.2)', borderRadius: '6px', fontSize: '12px' }}>
                            <span style={{ color: textPrimary, fontWeight: 500 }}>
                              {lg.self?.displayValue || 'عملية إدارية'}
                            </span>
                            <span style={{ color: lg.isOperationSuccess ? '#10b981' : '#ef4444', display: 'flex', alignItems: 'center', gap: '4px' }}>
                              {lg.isOperationSuccess ? <CheckCircle2 size={12} /> : <XCircle size={12} />}
                              {lg.createdAt ? new Date(lg.createdAt).toLocaleTimeString('ar-IQ') : ''}
                            </span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <span style={{ color: textSecondary, fontSize: '12px' }}>لا توجد سجلات حركات سابقة.</span>
                    )}
                  </div>

                  {/* Comments Section */}
                  <div style={{ background: isLight ? '#f8fafc' : 'rgba(255,255,255,0.03)', border: `1px solid ${borderColor}`, borderRadius: '10px', padding: '14px' }}>
                    <h3 style={{ color: '#3b82f6', fontSize: '14px', fontWeight: 600, margin: '0 0 10px 0', display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <MessageSquare size={15} /> التعليقات والملاحظات
                    </h3>
                    {detailsData?.comments?.items && detailsData.comments.items.length > 0 ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '12px' }}>
                        {detailsData.comments.items.map((c: any, idx: number) => (
                          <div key={idx} style={{ padding: '8px 10px', background: isLight ? '#e2e8f0' : 'rgba(0,0,0,0.25)', borderRadius: '6px', fontSize: '13px' }}>
                            <div style={{ color: textPrimary, marginBottom: '4px' }}>{c.content || c.body}</div>
                            <div style={{ color: textSecondary, fontSize: '11px' }}>{c.createdAt ? new Date(c.createdAt).toLocaleString('ar-IQ') : ''}</div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div style={{ color: textSecondary, fontSize: '12px', marginBottom: '12px' }}>لا توجد تعليقات سابقة.</div>
                    )}

                    {/* Add Comment Input */}
                    <form onSubmit={handleAddComment} style={{ display: 'flex', gap: '8px' }}>
                      <input
                        type="text"
                        value={newComment}
                        onChange={(e) => setNewComment(e.target.value)}
                        placeholder="إضافة ملاحظة أو تعليق على الطلب..."
                        style={{
                          flex: 1,
                          background: inputBg,
                          border: `1px solid ${inputBorder}`,
                          borderRadius: '6px',
                          padding: '8px 12px',
                          color: textPrimary,
                          fontSize: '12px',
                          outline: 'none',
                        }}
                      />
                      <button
                        type="submit"
                        disabled={isSubmittingComment || !newComment.trim()}
                        style={{
                          background: '#3b82f6',
                          border: 'none',
                          borderRadius: '6px',
                          padding: '8px 14px',
                          color: '#ffffff',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px',
                          fontSize: '12px',
                          opacity: isSubmittingComment || !newComment.trim() ? 0.5 : 1,
                        }}
                      >
                        <Send size={12} />
                        إرسال
                      </button>
                    </form>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default RequestsPage;